import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  tefImportFile,
  tefGetLastUpdate,
  tefGenerateResult,
  tefSendEmails,
  tefGetExtensionsHistory,
  repoGetLastUpdate,
  repoImport,
  repoExport,
} from '../actions/tefInternalThunks';
import './TefInternal.css';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  container: {
    marginTop: theme.spacing(3),
  },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  Upload: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Download: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Zap: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  Mail: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Clock: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  User: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Time: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  File: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/>
    </svg>
  ),
  X: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Excel: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#16a34a" strokeWidth="1.5"/>
      <polyline points="13 2 13 9 20 9" stroke="#16a34a" strokeWidth="1.5"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="#16a34a" strokeWidth="1.5"/>
      <line x1="8" y1="17" x2="16" y2="17" stroke="#16a34a" strokeWidth="1.5"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return value; }
};

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner({ dark = false }) {
  return <span className={`tef-spinner${dark ? ' tef-spinner-dark' : ''}`} />;
}

// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
function ActionButton({ iconColor, iconBg, IconComp, label, description, onClick, loading }) {
  return (
    <button className="tef-action-btn" onClick={onClick} disabled={loading}>
      <div className="tef-action-icon" style={{ background: iconBg }}>
        {loading ? <Spinner dark /> : <IconComp size={16} color={iconColor} />}
      </div>
      <span className="tef-action-btn-label">{label}</span>
      {description && <span className="tef-action-btn-desc">{description}</span>}
    </button>
  );
}

// ─── REPO HISTORY MODAL ───────────────────────────────────────────────────────
function RepoHistoryModal({ list, onClose }) {
  return (
    <div className="tef-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tef-modal" style={{ maxWidth: 500 }}>
        <div className="tef-modal-header">
          <div>
            <p className="tef-modal-title">Repository — upload history</p>
            <p className="tef-modal-subtitle">{list.length} record{list.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="tef-close-btn" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="tef-modal-body" style={{ padding: '12px 0', maxHeight: 360, overflowY: 'auto' }}>
          {list.length === 0 ? (
            <p className="tef-no-data" style={{ padding: '12px 20px' }}>No records available</p>
          ) : list.map((item, i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              padding: '10px 20px',
              borderBottom: i < list.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#94a3b8', display: 'flex' }}><Icon.User /></span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', fontFamily: "'Inter', sans-serif" }}>
                    {item.user ?? '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#94a3b8', display: 'flex' }}><Icon.Time /></span>
                  <span style={{ fontSize: 12, color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                    {formatTimestamp(item.timestamp) ?? '—'}
                  </span>
                </div>
              </div>
              {item.file && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span style={{ color: '#94a3b8', display: 'flex' }}><Icon.File /></span>
                  <span style={{ fontSize: 12, color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                    {item.file}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="tef-modal-footer">
          <button className="tef-btn tef-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── LAST UPDATE ─────────────────────────────────────────────────────────────
function LastUpdate({ user, timestamp, file, onLabelClick }) {
  const formatted = formatTimestamp(timestamp);
  const hasData   = user || formatted || file;
  const rows = [
    ...(user      ? [{ Icon: Icon.User, text: user }]      : []),
    ...(formatted ? [{ Icon: Icon.Time, text: formatted }]  : []),
    ...(file      ? [{ Icon: Icon.File, text: file }]       : []),
  ];

  return (
    <div>
      <p
        className="tef-last-update-label"
        onClick={onLabelClick}
        style={onLabelClick ? { cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 } : {}}
      >
        Last update
      </p>
      {hasData ? rows.map(({ Icon: Ic, text }, i) => (
        <div key={i} className="tef-last-update-row">
          <span className="tef-last-update-row-icon"><Ic /></span>
          <span className="tef-last-update-row-text">{text}</span>
        </div>
      )) : (
        <p className="tef-no-data">No data available</p>
      )}
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ title, subtitle, onClose, onConfirm, loading }) {
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError]       = useState('');
  const inputRef                = useRef(null);

  const MAX_SIZE_MB = 100;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const isValid = (f) => f.name.endsWith('.xlsx') || f.name.endsWith('.xls');

  const applyFile = (f) => {
    if (!f) return;
    if (!isValid(f)) { setError('Only .xlsx or .xls files are accepted.'); return; }
    if (f.size > MAX_SIZE_BYTES) { setError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`); return; }
    setError(''); setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]);
  }, []);

  return (
    <div className="tef-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tef-modal">
        <div className="tef-modal-header">
          <div>
            <p className="tef-modal-title">{title}</p>
            {subtitle && <p className="tef-modal-subtitle">{subtitle}</p>}
          </div>
          <button className="tef-close-btn" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="tef-modal-body">
          <div
            className={`tef-dropzone${dragOver ? ' drag-over' : ''}${file ? ' has-file' : ''}`}
            onClick={() => !file && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="tef-file-info">
                <Icon.Excel />
                <p className="tef-file-name">{file.name}</p>
                <p className="tef-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="tef-remove-btn" onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <>
                <Icon.Upload size={20} color={dragOver ? '#3b82f6' : '#94a3b8'} />
                <p className="tef-dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop or click to browse'}</p>
                <p className="tef-dropzone-sub">.xlsx or .xls — max 100MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef} type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => applyFile(e.target.files[0])}
            style={{ display: 'none' }}
          />
          {error && <p className="tef-error-msg">{error}</p>}
        </div>

        <div className="tef-modal-footer">
          <button className="tef-btn tef-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="tef-btn tef-btn-primary"
            onClick={() => file && onConfirm(file)}
            disabled={!file || loading}
          >
            {loading ? <><Spinner /> Uploading...</> : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SEND EMAILS MODAL ────────────────────────────────────────────────────────
function SendEmailsModal({ onClose, onConfirm, loading }) {
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError]       = useState('');
  const inputRef                = useRef(null);

  const MAX_SIZE_MB    = 100;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const isValid = (f) => f.name.endsWith('.xlsx') || f.name.endsWith('.xls');

  const applyFile = (f) => {
    if (!f) return;
    if (!isValid(f)) { setError('Only .xlsx or .xls files are accepted.'); return; }
    if (f.size > MAX_SIZE_BYTES) { setError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`); return; }
    setError(''); setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]);
  }, []);

  return (
    <div className="tef-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tef-modal" style={{ maxWidth: 480 }}>
        <div className="tef-modal-header">
          <div>
            <p className="tef-modal-title">Send emails</p>
            <p className="tef-modal-subtitle">Upload a file and choose the type of send</p>
          </div>
          <button className="tef-close-btn" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="tef-modal-body">
          {/* Drop zone */}
          <div
            className={`tef-dropzone${dragOver ? ' drag-over' : ''}${file ? ' has-file' : ''}`}
            style={{ marginBottom: 16 }}
            onClick={() => !file && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="tef-file-info">
                <Icon.Excel />
                <p className="tef-file-name">{file.name}</p>
                <p className="tef-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="tef-remove-btn" onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <>
                <Icon.Upload size={20} color={dragOver ? '#3b82f6' : '#94a3b8'} />
                <p className="tef-dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop or click to browse'}</p>
                <p className="tef-dropzone-sub">.xlsx or .xls — max 100MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef} type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => applyFile(e.target.files[0])}
            style={{ display: 'none' }}
          />
          {error && <p className="tef-error-msg" style={{ marginBottom: 12 }}>{error}</p>}

          {/* Send options */}
          <button className="tef-send-option" onClick={() => file && onConfirm(true, file)} disabled={loading || !file}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: !file ? '#f1f5f9' : '#eff6ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={!file ? '#94a3b8' : '#1d4ed8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/><path d="M20.618 5.984A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9c0-1.052-.12-2.076-.382-3.016z"/>
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <p className="tef-send-option-label">Test send emails</p>
                <p className="tef-send-option-desc">Sends to test addresses only — no real recipients affected</p>
              </div>
            </div>
          </button>

          <button className="tef-send-option" onClick={() => file && onConfirm(false, file)} disabled={loading || !file}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: !file ? '#f1f5f9' : '#f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={!file ? '#94a3b8' : '#16a34a'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <p className="tef-send-option-label">Send final emails</p>
                <p className="tef-send-option-desc">Sends to all real recipients — this action cannot be undone</p>
              </div>
            </div>
          </button>
        </div>

        <div className="tef-modal-footer">
          <button className="tef-btn tef-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── SUCCESS POPUP ────────────────────────────────────────────────────────────
function ResultPopup({ result, onClose }) {
  const isOk = result?.status === 'OK' || result?.Status === 'OK';
  const message = result?.message ?? result?.Message ?? 'Operation completed.';

  return (
    <div className="tef-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tef-modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <div className="tef-modal-body" style={{ padding: '32px 24px 24px' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: isOk ? '#f0fdf4' : '#fffbeb',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px',
          }}>
            {isOk ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            )}
          </div>
          <p style={{
            fontSize: 15, fontWeight: 600,
            color: isOk ? '#0f172a' : '#92400e',
            fontFamily: "'Inter', sans-serif", marginBottom: 6,
          }}>{isOk ? 'Success' : 'Warning'}</p>
          <p style={{
            fontSize: 13, color: '#64748b',
            fontFamily: "'Inter', sans-serif",
          }}>{message}</p>
        </div>
        <div className="tef-modal-footer" style={{ justifyContent: 'center', paddingTop: 0 }}>
          <button
            className="tef-btn tef-btn-primary"
            onClick={onClose}
            style={{ minWidth: 80, background: isOk ? '#1e40af' : '#d97706', borderColor: isOk ? '#1e40af' : '#d97706' }}
          >
            Ok
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function TefInternal() {
  const classes  = useStyles();
  const dispatch   = useDispatch();
  const loading         = useSelector((state) => state.tefInternalReducer.loading);
  const tef             = useSelector((state) => state.tefInternalReducer.tef);
  const repository      = useSelector((state) => state.tefInternalReducer.repository);
  const userId          = useSelector((state) => state.session.user?.id);
  const userEmail       = useSelector((state) => state.session.user?.email);
  const MODULE          = 'TEF Internal';

  const [modal, setModal]               = useState(null);
  const [successPopup, setSuccessPopup] = useState(false);
  const [repoHistory, setRepoHistory]   = useState(false);
  const prevSendEmailsResult            = useRef(null);

  useEffect(() => {
    dispatch(tefGetLastUpdate());
    dispatch(repoGetLastUpdate(MODULE));
  }, []);

  useEffect(() => {
    if (
      tef.sendEmailsResult &&
      tef.sendEmailsResult !== prevSendEmailsResult.current
    ) {
      prevSendEmailsResult.current = tef.sendEmailsResult;
      setSuccessPopup(true);
    }
  }, [tef.sendEmailsResult]);

  const handleTefUpload  = (file)             => { dispatch(tefImportFile(userId, file));                 setModal(null); };
  const handleRepoUpload = (file)             => { dispatch(repoImport(userId, MODULE, file));             setModal(null); };
  const handleSendEmails = (testEmails, file) => { dispatch(tefSendEmails(userId, userEmail, testEmails, file)); setModal(null); };

  return (
    <Page className={classes.root} title="TEF Internal">
      <Header module="NASP" view="TEF Internal" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="tef-root">

            {modal === 'tef' && (
              <UploadModal
                title="Import TEF file"
                subtitle="Only .xlsx or .xls files accepted — max 100MB"
                onClose={() => setModal(null)}
                onConfirm={handleTefUpload}
                loading={loading.importFile}
              />
            )}
            {modal === 'repo' && (
              <UploadModal
                title="Import repository"
                subtitle="Only .xlsx or .xls files accepted — max 100MB"
                onClose={() => setModal(null)}
                onConfirm={handleRepoUpload}
                loading={loading.repoImport}
              />
            )}
            {modal === 'sendEmails' && (
              <SendEmailsModal
                onClose={() => setModal(null)}
                onConfirm={handleSendEmails}
                loading={loading.sendEmails}
              />
            )}
            {repoHistory && (
              <RepoHistoryModal
                list={repository.list ?? []}
                onClose={() => setRepoHistory(false)}
              />
            )}
            {successPopup && (
              <ResultPopup
                result={tef.sendEmailsResult}
                onClose={() => setSuccessPopup(false)}
              />
            )}

            <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── TEF Internal Process ───────────────────────────────────── */}
              <div className="tef-card">
                <div className="tef-card-header">
                  <span className="tef-card-title">TEF Internal Process</span>
                  <span className="tef-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Active</span>
                </div>
                <div className="tef-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 220px', gap: '20px', alignItems: 'start' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <ActionButton
                          iconColor="#1d4ed8" iconBg="#eff6ff" IconComp={Icon.Upload}
                          label="Import file"
                          description="Upload a new .xlsx file"
                          onClick={() => setModal('tef')}
                          loading={loading.importFile}
                        />
                      </div>
                      <ActionButton
                        iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Zap}
                        label="Generate result"
                        description="Process current data"
                        onClick={() => dispatch(tefGenerateResult(userId))}
                        loading={loading.generateResult}
                      />
                      <ActionButton
                        iconColor="#7c3aed" iconBg="#faf5ff" IconComp={Icon.Mail}
                        label="Send emails"
                        description="Notify extensions"
                        onClick={() => setModal('sendEmails')}
                        loading={loading.sendEmails}
                      />
                      <div style={{ gridColumn: '1 / -1' }}>
                        <ActionButton
                          iconColor="#b45309" iconBg="#fffbeb" IconComp={Icon.Clock}
                          label="Extensions history"
                          description="View all past activities"
                          onClick={() => dispatch(tefGetExtensionsHistory(userId))}
                          loading={loading.extensionsHistory}
                        />
                      </div>
                    </div>

                    <div style={{ background: '#f1f5f9', width: 1, alignSelf: 'stretch' }} />

                    <LastUpdate
                      user={tef.lastUpdate.user}
                      timestamp={tef.lastUpdate.timestamp}
                    />
                  </div>
                </div>
              </div>

              {/* ── Repository ─────────────────────────────────────────────── */}
              <div className="tef-card">
                <div className="tef-card-header">
                  <span className="tef-card-title">Repository</span>
                  <span className="tef-badge" style={{ background: '#ecfeff', color: '#0e7490' }}>Sync</span>
                </div>
                <div className="tef-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 220px', gap: '20px', alignItems: 'start' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <ActionButton
                        iconColor="#1d4ed8" iconBg="#eff6ff" IconComp={Icon.Upload}
                        label="Import"
                        description="Upload repository file"
                        onClick={() => setModal('repo')}
                        loading={loading.repoImport}
                      />
                      <ActionButton
                        iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Download}
                        label="Export"
                        description="Download current data"
                        onClick={() => dispatch(repoExport(userId, MODULE))}
                        loading={loading.repoExport}
                      />
                    </div>

                    <div style={{ background: '#f1f5f9', width: 1, alignSelf: 'stretch' }} />

                    <LastUpdate
                      user={repository.lastUpdate.user}
                      timestamp={repository.lastUpdate.timestamp}
                      file={repository.lastUpdate.file}
                      onLabelClick={() => setRepoHistory(true)}
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default TefInternal;